/**
 * One canonical runtime guard module for `apps/web`.
 *
 * External JSON (API responses, `openapi.json`) arrives as `unknown`; every read
 * of it goes through these helpers instead of an ad-hoc `typeof` chain per call
 * site. Field-level validation stays explicit at the boundary adapter
 * (`src/api/contract.ts`).
 */

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export function asNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

export function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** First key present with a non-null value — used where the wire name may vary. */
export function pick(record: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    if (key in record) {
      const value = record[key];
      if (value !== undefined && value !== null) return value;
    }
  }
  return undefined;
}
